import React from "react";
import {
  Tooltip as MuiTooltip,
  TextField as MuiTextField,
  Select as MuiSelect,
  TooltipProps,
  BoxProps,
  Box,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tokens } from "../../../theme/tokens";

const focusRing = {
  outline: `${tokens.focus.width} solid ${tokens.color.focus.ring}`,
  outlineOffset: tokens.focus.offset,
} as const;

export const StyledTooltip: React.FC<TooltipProps> = (props) => (
  <MuiTooltip
    {...props}
    componentsProps={{
      tooltip: {
        sx: {
          backgroundColor: tokens.color.text.primary,
          color: tokens.color.brand.onPrimary,
          fontSize: tokens.type.caption.size,
          fontWeight: tokens.type.caption.weight,
          lineHeight: tokens.type.caption.lineHeight,
          borderRadius: tokens.radius.sm,
          padding: `${tokens.space[1]} ${tokens.space[2]}`,
          boxShadow: "none",
        },
      },
      arrow: { sx: { color: tokens.color.text.primary } },
    }}
  />
);

export const Field = styled(MuiTextField)({
  "& .MuiOutlinedInput-root": {
    backgroundColor: tokens.color.surface.default,
    borderRadius: tokens.radius.sm,
    "& fieldset": {
      borderColor: tokens.color.border.default,
      borderWidth: tokens.borderWidth,
    },
    "&:hover fieldset": {
      borderColor: tokens.color.border.strong,
    },
    "&.Mui-focused fieldset": {
      borderColor: tokens.color.focus.ring,
      borderWidth: tokens.focus.width,
    },
    "&.Mui-error fieldset": {
      borderColor: tokens.color.status.error,
    },
    "&.Mui-disabled fieldset": {
      borderColor: tokens.color.border.default,
    },
  },
  "& .MuiInputBase-input": {
    color: tokens.color.text.primary,
    fontSize: tokens.type.dense.size,
  },
  "& .MuiInputLabel-root": {
    color: tokens.color.text.secondary,
    fontSize: tokens.type.caption.size,
    "&.Mui-focused": { color: tokens.color.focus.ring },
    "&.Mui-error": { color: tokens.color.status.error },
  },
  "& .MuiFormHelperText-root.Mui-error": {
    color: tokens.color.status.error,
  },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: tokens.color.text.disabled,
  },
});

export const Select = styled(MuiSelect)({
  backgroundColor: tokens.color.surface.default,
  borderRadius: tokens.radius.sm,
  color: tokens.color.text.primary,
  fontSize: tokens.type.dense.size,
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: tokens.color.border.default,
    borderWidth: tokens.borderWidth,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: tokens.color.border.strong,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: tokens.color.focus.ring,
    borderWidth: tokens.focus.width,
  },
  "&.Mui-error .MuiOutlinedInput-notchedOutline": {
    borderColor: tokens.color.status.error,
  },
  "&.Mui-disabled": { color: tokens.color.text.disabled },
}) as typeof MuiSelect;

type SurfaceProps = BoxProps & { interactive?: boolean };

export const Surface = React.forwardRef<HTMLDivElement, SurfaceProps>(
  ({ interactive, sx, ...props }, ref) => (
    <Box
      ref={ref}
      {...props}
      sx={{
        backgroundColor: tokens.color.surface.default,
        border: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
        borderRadius: tokens.radius.md,
        boxShadow: "none",
        ...(interactive
          ? {
              transition: `background-color ${tokens.motion.fast} ${tokens.motion.easing}`,
              "@media (prefers-reduced-motion: reduce)": { transition: "none" },
              "&:hover": { backgroundColor: tokens.color.surface.hover },
              "&:focus-visible": focusRing,
            }
          : {}),
        ...sx,
      }}
    />
  )
);
Surface.displayName = "Surface";
